import React from 'react'
import { IUserSelections, IEventRegistrationRefreshModel, IEpiProps } from '../models/event-reg-refresh-models'
import { buildDateRangeString, getDateTimeEventLocation } from '../helpers/helpers'
import { useEventRegRefreshContext } from '../reducers/event-reg-refresh-reducers'

interface IProps {
  userSelections: IUserSelections
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
  showPricing: boolean
  formatCurrency: (amount: number) => string
}

interface IContextProps {
  epiProps: IEpiProps
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
}

export const ProgramSummarySelections = (props: IProps) => {
  const { epiProps, eventRegistrationRefreshModel }: IContextProps = useEventRegRefreshContext()!

  // Check what content should be shown
  const showSelectedProgram = props.userSelections
    && props.userSelections.userProgramSelection
    && props.userSelections.userProgramSelection.programName
    && props.userSelections.userProgramSelection.programName.length > 0;

  const selectedAddOnSelections = props.userSelections.userAddOnProgramSelections || []
  const showSelectedAddOnPrograms = selectedAddOnSelections.length > 0

  const showSelectedProgramDateTimeSelection = props.userSelections
    && props.userSelections.userProgramDateTimeSelection
    && getDateTimeEventLocation(props.userSelections.userProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard)
    && getDateTimeEventLocation(props.userSelections.userProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard).length > 0;

  const showSelectedAddOnProgramDateTimeSelection = props.userSelections
    && props.userSelections.userAddOnProgramDateTimeSelection
    && getDateTimeEventLocation(props.userSelections.userAddOnProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard)
    && getDateTimeEventLocation(props.userSelections.userAddOnProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard).length > 0;

  // Get selected program details for pricing
  const selectedProgram = props.eventRegistrationRefreshModel.specialtyPrograms.find(
    p => p.programCode === props.userSelections.userProgramSelection?.programCode &&
      p.isRenewal === props.userSelections.userProgramSelection.isRenewal)

  const attendeeCount = props.userSelections.attendees?.length || 0
  const programPrice = props.userSelections.userProgramDateTimeSelection?.price || 0
  const totalProgramPrice = programPrice * attendeeCount

  // Sum prices across all selected add-ons; product-based use static price, event-based use datetime price
  const totalAddOnPrice = selectedAddOnSelections.reduce((sum, sel) => {
    const addonDef = selectedProgram?.addOnPrograms?.find(a =>
      sel.programSku && a.programSku ? sel.programSku === a.programSku : sel.programCode === a.programCode
    )
    const pricePerAttendee = sel.disableDateTimeSelection
      ? (addonDef?.programPrice ?? 0)
      : (props.userSelections.userAddOnProgramDateTimeSelection?.price ?? addonDef?.programPrice ?? 0)
    return sum + pricePerAttendee
  }, 0) * attendeeCount

  const totalPrice = totalProgramPrice + totalAddOnPrice

  // Check if there are any selections
  const hasSelections = showSelectedProgram || showSelectedAddOnPrograms

  return (
    <div>
      {!hasSelections && (
        <div className="summary-card-section">
          <p>{epiProps.noSelectionsMessageProgramSummary}</p>
        </div>
      )}

      {/* Selected Program */}
      {showSelectedProgram && (
        <div className="summary-card-section">
          <p className="large">
            <strong>{props.userSelections.userProgramSelection.programName}</strong>
          </p>

          {/* Location and Date */}
          {showSelectedProgramDateTimeSelection && (
            <div className="summary-card-row">
              <p>{getDateTimeEventLocation(props.userSelections.userProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard)}</p>
              <p>{buildDateRangeString(props.userSelections.userProgramDateTimeSelection.days, eventRegistrationRefreshModel.selectedLanguage)}</p>
            </div>
          )}

          {/* Price Breakdown for Attendees Step */}
          {props.showPricing && (
            <div className="summary-pricing">
              <div className="summary-card-row">
                <span>{attendeeCount} {attendeeCount !== 1 ? epiProps.multipleAttendeesLabelProgramSummary : epiProps.attendeeLabelProgramSummary}</span>
                <span>{props.formatCurrency(totalProgramPrice)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Selected Add-Ons */}
      {showSelectedAddOnPrograms && selectedAddOnSelections.map((sel) => {
        const addonDef = selectedProgram?.addOnPrograms?.find(a =>
          sel.programSku && a.programSku ? sel.programSku === a.programSku : sel.programCode === a.programCode
        )
        const isEventBased = !sel.disableDateTimeSelection
        const addonPrice = isEventBased
          ? (props.userSelections.userAddOnProgramDateTimeSelection?.price ?? addonDef?.programPrice ?? 0)
          : (addonDef?.programPrice ?? 0)
        const addonLinePrice = addonPrice * attendeeCount

        return (
          <div key={sel.programSku || sel.programCode} className="summary-card-section">
            <p className="large"><strong>{sel.programName}</strong></p>

            {/* Date/time info only for event-based add-ons */}
            {isEventBased && showSelectedAddOnProgramDateTimeSelection && (
              <>
                <div className="summary-card-row">
                  <p>{getDateTimeEventLocation(props.userSelections.userAddOnProgramDateTimeSelection, epiProps.virtualLocationTextDateTimeCard)}</p>
                </div>
                <div className="summary-card-row">
                  <p>{buildDateRangeString(props.userSelections.userAddOnProgramDateTimeSelection.days, eventRegistrationRefreshModel.selectedLanguage)}</p>
                </div>
              </>
            )}

            {props.showPricing && addonDef && (
              <div className="summary-card-row">
                <span>{attendeeCount} {attendeeCount !== 1 ? epiProps.multipleAttendeesLabelProgramSummary : epiProps.attendeeLabelProgramSummary}</span>
                <span>{props.formatCurrency(addonLinePrice)}</span>
              </div>
            )}
          </div>
        )
      })}

      {/* Total Price (only show when pricing is shown) */}
      {props.showPricing && (
        <div className="summary-card-row summary-card-row-totals summary-card-section">
          <p className="summary-total-label">{epiProps.programTotalLabelProgramSummary}</p>
          <p className="summary-total-amount">{props.formatCurrency(totalPrice)}</p>
        </div>
      )}
    </div>
  )
}
